import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Wallet, Users, Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Brand";
import { StatusBadge } from "@/components/store/HistoryTab";

const NAV = [["redemptions", "Redemptions", Wallet], ["purchases", "Purchases", Receipt], ["users", "Users", Users]];
const ACTIONS = { pending: ["approved", "rejected"], approved: ["paid", "pending", "rejected"] };
const ACT_LABEL = { approved: "Approve", rejected: "Reject", paid: "Mark Paid", pending: "Set Pending" };
const ACT_CLS = { approved: "bg-[#00A3FF]", rejected: "bg-[#EF4444]", paid: "bg-[#10B981] text-[#0B0E14]", pending: "bg-[#F5A524] text-[#0B0E14]" };
const d = (s) => new Date(s).toLocaleString();

function Stats({ s }) {
  const items = [["Players", s.users], ["Purchases", `$${s.purchase_total.toLocaleString()}`], ["Pending payouts", `$${s.pending_total.toLocaleString()} (${s.pending_count})`], ["Paid out", `$${s.paid_total.toLocaleString()}`]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(([l, v]) => (
        <div key={l} className="rounded-2xl bg-[#151A22] border border-white/5 p-4" data-testid={`admin-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
          <div className="text-xs uppercase tracking-wider text-gray-500">{l}</div>
          <div className="mt-1 font-display text-2xl font-black">{v}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const { signOut } = useApp();
  const nav = useNavigate();
  const [view, setView] = useState("redemptions");
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);

  const load = useCallback(async (isStale = () => false) => {
    const url = view === "redemptions" ? `/admin/redemptions?status=${filter}` : `/admin/${view}`;
    const [r, s] = await Promise.all([api.get(url), api.get("/admin/stats")]);
    if (isStale()) return;
    setRows(r.data); setStats(s.data);
  }, [view, filter]);
  useEffect(() => { let stale = false; load(() => stale); return () => { stale = true; }; }, [load]);

  const act = async (id, status) => {
    try { await api.post(`/admin/redemptions/${id}/status`, { status }); toast.success(`Redemption ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="relative z-10 min-h-screen flex flex-col md:flex-row" data-testid="admin-page">
      <aside className="md:w-60 shrink-0 border-b md:border-b-0 md:border-r border-white/5 bg-[#151A22] p-5 flex md:flex-col gap-2 md:gap-1 overflow-x-auto">
        <Logo className="mb-0 md:mb-6 mr-4 md:mr-0" />
        {NAV.map(([k, l, I]) => (
          <button key={k} data-testid={`admin-nav-${k}`} onClick={() => { if (k !== view) { setRows([]); setView(k); } }} className={`shrink-0 flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold transition-colors ${view === k ? "bg-[#FFB800]/15 gc-text" : "text-gray-400 hover:text-white"}`}><I size={16} />{l}</button>
        ))}
        <button data-testid="admin-logout" onClick={() => { signOut(); nav("/admin/login"); }} className="shrink-0 md:mt-auto flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-gray-500 hover:text-white transition-colors"><LogOut size={16} />Log out</button>
      </aside>
      <main className="flex-1 p-4 sm:p-6 space-y-6 overflow-x-auto">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-black capitalize">{view}</h1>
          <div className="flex items-center gap-2">
            {view === "redemptions" && (
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-36 bg-[#151A22] border-white/10" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#151A22] border-white/10">
                  {["all", "pending", "approved", "paid", "rejected"].map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`admin-filter-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <button data-testid="admin-refresh" onClick={() => load()} className="p-2.5 rounded-xl bg-[#151A22] border border-white/10 hover:bg-[#1F2633] transition-colors"><RefreshCw size={16} /></button>
          </div>
        </div>
        {stats && <Stats s={stats} />}
        <div className="rounded-2xl border border-white/5 bg-[#151A22] overflow-x-auto">
          <Table>
            <TableHeader>
              {view === "redemptions" && <TableRow className="border-white/5"><TableHead>Date</TableHead><TableHead>Player</TableHead><TableHead>Amount</TableHead><TableHead>Destination</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>}
              {view === "purchases" && <TableRow className="border-white/5"><TableHead>Date</TableHead><TableHead>Player</TableHead><TableHead>Amount</TableHead><TableHead>Card</TableHead><TableHead>Credited</TableHead><TableHead>Status</TableHead></TableRow>}
              {view === "users" && <TableRow className="border-white/5"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>GC</TableHead><TableHead>SC</TableHead><TableHead>VIP</TableHead></TableRow>}
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-10" data-testid="admin-empty">No records</TableCell></TableRow>}
              {view === "redemptions" && rows.map((r) => (
                <TableRow key={r.id} className="border-white/5" data-testid={`admin-redemption-${r.id}`}>
                  <TableCell className="text-xs text-gray-400 whitespace-nowrap">{d(r.created_at)}</TableCell>
                  <TableCell><div className="font-semibold">{r.account_name}</div><div className="text-xs text-gray-500">{r.user_email}</div></TableCell>
                  <TableCell className="font-display font-bold sc-text">${r.amount.toFixed(2)}</TableCell>
                  <TableCell className="text-sm">{r.destination}</TableCell>
                  <TableCell><StatusBadge s={r.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    {(ACTIONS[r.status] || []).map((a) => (
                      <button key={a} data-testid={`admin-action-${a}-${r.id}`} onClick={() => act(r.id, a)} className={`rounded-lg px-2.5 py-1 text-xs font-bold hover:brightness-110 transition-[filter] ${ACT_CLS[a]}`}>{ACT_LABEL[a]}</button>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
              {view === "purchases" && rows.map((r) => (
                <TableRow key={r.id} className="border-white/5" data-testid={`admin-purchase-${r.id}`}>
                  <TableCell className="text-xs text-gray-400 whitespace-nowrap">{d(r.created_at)}</TableCell>
                  <TableCell>{r.user_email}</TableCell>
                  <TableCell className="font-display font-bold">${r.amount.toFixed(2)}</TableCell>
                  <TableCell>{r.card_brand} ••{r.card_last4}</TableCell>
                  <TableCell className="text-sm"><span className="gc-text">{r.gc.toLocaleString()} GC</span> + <span className="sc-text">{r.sc} SC</span></TableCell>
                  <TableCell><StatusBadge s={r.status} /></TableCell>
                </TableRow>
              ))}
              {view === "users" && rows.map((u) => (
                <TableRow key={u.id} className="border-white/5" data-testid={`admin-user-${u.id}`}>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell className="capitalize">{u.role}</TableCell>
                  <TableCell className="gc-text">{Math.floor(u.gc).toLocaleString()}</TableCell>
                  <TableCell className="sc-text">{u.sc.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>{u.vip.tier}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
